import React, { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import Home from "@/pages/Home";
import CategoryBrowse from "@/pages/CategoryBrowse";
import ListingDetail from "@/pages/ListingDetail";
import CartPage from "@/pages/CartPage";
import CheckoutPage from "@/pages/CheckoutPage";
import BookingConfirmation from "@/pages/BookingConfirmation";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import TouristDashboard from "@/pages/TouristDashboard";
import VendorDashboard from "@/pages/VendorDashboard";
import VendorListings from "@/pages/VendorListings";
import VendorBookings from "@/pages/VendorBookings";
import VendorOnboarding from "@/pages/VendorOnboarding";
import BecomeVendor from "@/pages/BecomeVendor";
import PaymentPage from "@/pages/PaymentPage";
import PaymentResult from "@/pages/PaymentResult";
import AdminDashboard from "@/pages/AdminDashboard";
import VendorMessages from "@/pages/VendorMessages";
import VendorReconciliation from "@/pages/VendorReconciliation";

const ProtectedRoute = ({ children, role }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="p-10 text-center text-muted-foreground">Loading…</div>;
  if (!user) return <Navigate to="/auth/login" replace />;
  if (role && user.role !== role) return <Navigate to="/" replace />;
  return children;
};

const AuthHashHandler = () => {
  const { exchangeGoogleSession } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const processed = React.useRef(false);
  useEffect(() => {
    if (processed.current) return;
    const hash = location.hash || window.location.hash;
    if (hash && hash.includes("session_id=")) {
      processed.current = true;
      const match = hash.match(/session_id=([^&]+)/);
      const session_id = match ? decodeURIComponent(match[1]) : null;
      if (session_id) {
        (async () => {
          try {
            const u = await exchangeGoogleSession(session_id);
            // clear hash
            window.history.replaceState(null, "", window.location.pathname + window.location.search);
            if (u.role === "vendor") navigate("/vendor", { replace: true });
            else navigate("/dashboard", { replace: true });
          } catch (e) {
            navigate("/auth/login?error=google", { replace: true });
          }
        })();
      }
    }
  }, [location.hash, navigate, exchangeGoogleSession]);
  return null;
};

function AppRoutes() {
  return (
    <>
      <AuthHashHandler />
      <Routes>
        <Route element={<Layout />}>
          <Route index element={<Home />} />
          <Route path="categories/:categoryId" element={<CategoryBrowse />} />
          <Route path="search" element={<CategoryBrowse />} />
          <Route path="listings/:listingId" element={<ListingDetail />} />
          <Route path="cart" element={<ProtectedRoute><CartPage /></ProtectedRoute>} />
          <Route path="checkout" element={<ProtectedRoute><CheckoutPage /></ProtectedRoute>} />
          <Route path="payments/:groupId" element={<ProtectedRoute><PaymentPage /></ProtectedRoute>} />
          <Route path="payments/success" element={<ProtectedRoute><PaymentResult status="success" /></ProtectedRoute>} />
          <Route path="payments/cancel" element={<ProtectedRoute><PaymentResult status="cancel" /></ProtectedRoute>} />
          <Route path="booking-confirmation/:groupId" element={<ProtectedRoute><BookingConfirmation /></ProtectedRoute>} />
          <Route path="dashboard" element={<ProtectedRoute><TouristDashboard /></ProtectedRoute>} />
          <Route path="become-a-vendor" element={<BecomeVendor />} />
          <Route path="vendor" element={<ProtectedRoute><VendorDashboard /></ProtectedRoute>} />
          <Route path="vendor/onboarding" element={<ProtectedRoute><VendorOnboarding /></ProtectedRoute>} />
          <Route path="vendor/listings" element={<ProtectedRoute><VendorListings /></ProtectedRoute>} />
          <Route path="vendor/bookings" element={<ProtectedRoute><VendorBookings /></ProtectedRoute>} />
          <Route path="vendor/messages" element={<ProtectedRoute><VendorMessages /></ProtectedRoute>} />
          <Route path="vendor/reconciliation" element={<ProtectedRoute><VendorReconciliation /></ProtectedRoute>} />
          <Route path="admin" element={<ProtectedRoute role="admin"><AdminDashboard /></ProtectedRoute>} />
          <Route path="auth/login" element={<Login />} />
          <Route path="auth/register" element={<Register />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
    </>
  );
}

export default function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
          <Toaster position="top-right" richColors closeButton />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}
